/**
 * Data-mark colours for the redesigned Public Sentiment report (SPEC §1–§2).
 *
 * Colour means STANCE; ink means AMOUNT. These are data encodings, not theme
 * chrome, so they do not follow a preset's brand colours (the house red/green
 * pair failed the deutan check, SPEC §2). Chrome — rules, labels, text — still
 * comes from `roleColor(theme, …)`; only the marks read this module.
 *
 * Validated with the dataviz palette validator:
 *  light (surface #ffffff) — stance poles CVD ΔE 15.6 / normal 21.4; stack
 *    #8a3b32 · #b9b8b2 · #046b96 CVD + normal PASS (gray midpoint band/chroma
 *    FAIL is expected; contrast WARN relieved by labels + table view); ink ramps
 *    ordinal PASS; reasons pair #2f2e2a/#878682 ΔE 31.9.
 *  dark (surface #0b0f14, the reader's dark page) — poles #d0796b/#3d9bd1 CVD
 *    ΔE 14.9 / normal 22.4; stack with #6b6a66 CVD 10.1 / normal 18.0; amount
 *    ramp #4d4c48 → #cfcec8 ordinal PASS (light end 2.24:1, every cell is
 *    count-labelled); reasons pair #e6e5df/#8a8984 ΔE 29.2.
 *
 * Export and print always resolve `light` (the reader mode is on-screen only).
 * Every `on*` colour is the text colour for a label drawn INSIDE that fill;
 * text outside a mark always uses theme ink, never a data colour.
 */
import { READER_DARK_PAGE_BG, type ReportReaderMode } from '../reportRoleColors';

export interface PsrVizPalette {
  opposed: string;
  neutral: string;
  supportive: string;
  /** Unscored records: never a stack segment, always count-labelled. */
  unscored: string;
  onOpposed: string;
  onNeutral: string;
  onSupportive: string;
  onUnscored: string;
  /** Ordinal ink ramp for AMOUNT, least → most. */
  amount: readonly [string, string, string, string];
  onAmount: readonly [string, string, string, string];
  /** Zero-count heatmap cell and the unread remainder of a funnel bar. */
  empty: string;
  onEmpty: string;
  /** 8% washes behind the ≥65 / ≤35 bands of the trend chart. */
  supportiveBandWash: string;
  opposedBandWash: string;
  /** The page surface: gaps between touching marks and marker rings. */
  surface: string;
}

const LIGHT: PsrVizPalette = Object.freeze({
  opposed: '#8a3b32',
  neutral: '#b9b8b2',
  supportive: '#046b96',
  unscored: '#e4e3de',
  onOpposed: '#ffffff',
  onNeutral: '#19191a',
  onSupportive: '#ffffff',
  onUnscored: '#19191a',
  amount: Object.freeze(['#afaea9', '#878682', '#5e5e59', '#2f2e2a'] as const),
  onAmount: Object.freeze(['#19191a', '#19191a', '#ffffff', '#ffffff'] as const),
  empty: '#f4f4f4',
  onEmpty: '#19191a',
  supportiveBandWash: 'rgba(4,107,150,0.08)',
  opposedBandWash: 'rgba(138,59,50,0.08)',
  surface: '#ffffff',
});

const DARK: PsrVizPalette = Object.freeze({
  opposed: '#d0796b',
  neutral: '#6b6a66',
  supportive: '#3d9bd1',
  unscored: '#2c2d30',
  onOpposed: '#0b0f14',
  onNeutral: '#ffffff',
  onSupportive: '#0b0f14',
  onUnscored: '#e6e5df',
  amount: Object.freeze(['#4d4c48', '#6f6e69', '#9a9994', '#cfcec8'] as const),
  onAmount: Object.freeze(['#ffffff', '#ffffff', '#0b0f14', '#0b0f14'] as const),
  empty: '#171a1f',
  onEmpty: '#e6e5df',
  supportiveBandWash: 'rgba(61,155,209,0.10)',
  opposedBandWash: 'rgba(208,121,107,0.10)',
  surface: READER_DARK_PAGE_BG,
});

export function psrVizPalette(mode: ReportReaderMode = 'light'): PsrVizPalette {
  return mode === 'dark' ? DARK : LIGHT;
}

/** Stance fill + in-mark text colour for −1 / 0 / +1. */
export function psrStanceColors(palette: PsrVizPalette, stance: -1 | 0 | 1): { fill: string; text: string } {
  if (stance < 0) return { fill: palette.opposed, text: palette.onOpposed };
  if (stance > 0) return { fill: palette.supportive, text: palette.onSupportive };
  return { fill: palette.neutral, text: palette.onNeutral };
}
